import { createSignal } from "solid-js";
import {
  negotiateLocale,
  SUPPORTED_LOCALES,
  Translator,
  type Locale,
  type Params,
} from "@energyplatform/i18n";

const STORAGE_KEY = "ep.locale";
declare const __BUILD_ID__: string;

const translator = new Translator({
  baseUrl: `${import.meta.env.BASE_URL}i18n`,
  namespaces: ["common"],
  version: __BUILD_ID__,
});

function initialLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && (SUPPORTED_LOCALES as readonly string[]).includes(saved)) {
      return saved as Locale;
    }
  } catch {
    // Storage may be blocked; fall back to the browser preference.
  }
  return negotiateLocale(navigator.languages);
}

const [locale, setLocaleSignal] = createSignal<Locale>(initialLocale());

export { locale, SUPPORTED_LOCALES };

export async function initI18n(): Promise<void> {
  await translator.load(locale());
  document.documentElement.lang = locale();
}

export async function setLocale(next: Locale): Promise<void> {
  await translator.load(next);
  setLocaleSignal(next);
  document.documentElement.lang = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Not persisted; the choice still applies to this session.
  }
}

/** Reactive: reading `locale()` makes every caller re-render when the language changes. */
export const t = (key: string, params?: Params): string =>
  translator.t(locale(), key, params);

/** Translation for a key, or the given fallback when no translation exists (e.g. unit symbols). */
export const tOr = (key: string, fallback: string, params?: Params): string =>
  translator.has(locale(), key)
    ? translator.t(locale(), key, params)
    : fallback;

/** API errors are codes with parameters; the text lives in translations under `errors.*`. */
export const errorText = (
  code: string,
  params: Record<string, unknown> = {},
): string =>
  tOr(`errors.${code}`, t("errors.unknown", { code }), params as Params);
